'use client';

import { createContext, useContext } from 'react';

/**
 * The Woodside mark, from apps/platform/public/icon.svg.
 *
 * Brand green by default, which is what it is. `tone="current"` hands the
 * fill to `currentColor` for the one case that needs it: a page wearing an
 * event's own accent, where the mark is standing in for "every campus" rather
 * than for the church's identity, and a teal W beside an orange page is the
 * only thing on it that did not get the memo. An individual campus's own art
 * is never recoloured — that IS their identity.
 */
export function WoodsideLogo({
  className = 'h-6 w-6',
  tone = 'brand',
}: {
  className?: string;
  tone?: 'brand' | 'current';
}) {
  const fill = tone === 'current' ? 'currentColor' : '#299c8f';
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 822.73 822.41"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill={fill}
        d="M482.59,292.96c-28.5,75.56-63.52,148.62-91.88,224.24-22.85,60.93-44.5,165.54,5.99,218.03,53.19,55.31,103.27-36.03,126.36-76.12,29.77-51.67,60.19-102.91,92.51-153.1,37.77-58.65,82.78-117.18,128.05-170.34,17.33-20.35,35.58-39.9,55.18-58.05,1.32-.3,1.67.72,2.19,1.61,2.7,4.68,6.16,19.72,7.79,25.79,55.59,207.53-59.67,424.44-261.39,494.49-162.86,56.55-343.5,6.03-452.97-125.71l.02-2.82c22.1-29.38,43.34-59.51,66.31-88.22,46.87-58.59,104.84-117,159.18-168.95,39.21-37.49,94.79-86.04,141.88-112.38,2.97-1.66,18.74-10.3,20.79-8.46Z"
      />
      <path
        fill={fill}
        d="M454.78,615.29c-.4-37.26,12.31-73.93,23.96-108.91,21.35-64.11,58.46-144.93,65.26-211.05,10.09-98.15-75.84-54.82-121.59-23.71-87.22,59.32-157.97,140.42-238.72,207.44-1.08.9-1.56,2.33-3.36,1.91,29.91-61.5,79.75-118.22,92.63-187.03,26.62-142.2-143-109.97-223.13-77.75-1.54-1.51,19.5-33.71,21.85-37.14C170.36,35.21,348.48-31.19,518.31,14.05c111.97,29.83,206.98,107.78,259.7,210.54l-1.23,3.19c-101.38,85.68-182.57,188.93-258.5,297.03-21.17,30.14-40.81,61.47-63.5,90.48Z"
      />
      <path
        fill={fill}
        d="M38.3,581.71c-6.2-9.05-10.4-20.99-14.14-31.42C-1.72,478.2-6.79,400.44,8.86,325.38c1.73-8.3,5.99-29.98,9.5-36.56,1.25-2.35,11.96-9.93,14.86-12.01,41.76-29.96,121.9-63.33,173.22-50.74,49.51,12.15,15.29,70.69-.39,97.86-34.22,59.31-78.86,114.75-116.32,172.48-18.06,27.83-35.65,56.1-51.43,85.3Z"
      />
    </svg>
  );
}

/**
 * Congregation_ID -> that campus's brand SVG, from /api/locations. Provided
 * once at the top of the finder so every chip and header can draw the right
 * icon without prop-drilling.
 */
const CampusSvgContext = createContext<Map<number, string>>(new Map());

export const CampusSvgProvider = CampusSvgContext.Provider;

/** The whole map, for a map of several campuses at once (CarePanel's pins). */
export function useCampusSvgMap(): Map<number, string> {
  return useContext(CampusSvgContext);
}

export function useCampusSvg(congregationId: number | null | undefined): string | null {
  const map = useContext(CampusSvgContext);
  if (congregationId === null || congregationId === undefined) return null;
  return map.get(congregationId) ?? null;
}

/**
 * A campus's icon: its brand SVG when the location has one, otherwise the
 * Woodside mark (also what "All campuses" shows).
 */
export function CampusIcon({
  congregationId,
  className = 'h-5 w-5',
}: {
  congregationId: number | null;
  className?: string;
}) {
  const svgUrl = useCampusSvg(congregationId);
  if (svgUrl) {
    return <img src={svgUrl} alt="" className={`${className} object-contain`} draggable={false} />;
  }
  return <WoodsideLogo className={className} />;
}
